
import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useImportanceColors } from "@/hooks/useImportanceColors";
import { importanceBadges } from "@/components/contracts/table/types";
import { DirectionText, getDirectionIcon } from "@/components/contract-types/ContractDirectionIcons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ContractDirection } from "@/lib/types";

interface ContractHeaderProps {
  id: string;
  supplierName: string;
  organisationnumber?: string;
  importance: number;
  typeName: string;
  categoryName?: string;
  onDelete: () => void;
  contractDirection?: ContractDirection;
}

export function ContractHeader({
  id,
  supplierName,
  organisationnumber,
  importance,
  typeName,
  categoryName,
  onDelete,
  contractDirection,
}: ContractHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getImportanceColor } = useImportanceColors();
  
  const importanceLabel = importanceBadges[importance]?.label || `Importance ${importance}`;
  
  const { className, style } = getImportanceColor(importance);
  
  return (
    <div className="space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/contracts")}
            className="p-2"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <h1 className="text-3xl font-bold">
            {supplierName}
          </h1>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(`/contracts/${id}/edit`)}
          >
            <Edit className="mr-2 h-4 w-4" />
            {t('edit')}
          </Button>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={onDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('delete')}
          </Button>
        </div>
      </div>
      
      {organisationnumber && (
        <p className="text-muted-foreground -mt-2">
          {t('organizationNumber')}: {organisationnumber}
        </p>
      )}
      
      <div className="flex flex-wrap gap-2 mt-2">
        <Badge 
          className={className}
          style={style}
        >
          {importanceLabel}
        </Badge>
        
        {contractDirection ? (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="flex items-center gap-1.5">
                  {getDirectionIcon(contractDirection, "h-3.5 w-3.5 mr-1")}
                  {typeName}
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p><DirectionText direction={contractDirection} /></p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Badge variant="outline">{typeName}</Badge>
        )}
        
        {categoryName && <Badge variant="secondary">{categoryName}</Badge>}
      </div>
    </div>
  );
}
